import { Injectable } from '@angular/core';
import { FrustrationId, GoalId, Quiz, highlightText } from 'funnel/src/app/data/quiz';
import {
  FunnelFlowService,
  QuizStepKey,
} from '../../../../funnel/src/app/services/funnel-flow.service';
import { FunnelFlowConfig } from 'funnel/src/app/flow/flow-config';
import { googleFunnelFlow } from '../google-funnel.flow';

declare const $localize: any;

@Injectable({
  providedIn: 'root',
})
export class GoogleFunnelFlowService extends FunnelFlowService {
  // The first screen stays as designed; an ad's `?title=` is ignored here.
  protected override readonly isCustomIntroTitleSupported = false;

  protected override getFlowConfig(): FunnelFlowConfig {
    return googleFunnelFlow;
  }

  protected override getDefaultIntroTitle(): string {
    return $localize`:@@googleIntroTitle:Turn any text into a natural audiobook - instantly`;
  }

  override customizeStepData(key: QuizStepKey): Quiz | undefined {
    if (key === 'age') {
      return {
        ...this.steps.age,
        isQuizBadgeShown: true,
        subtitle: undefined,
        title: this.getIntroTitle(),
      };
    }

    if (key === 'goals') {
      return {
        ...this.steps.goals,
        title:
          $localize`:@@googleGoalsTitlePrefix:What for do you need ` +
          highlightText($localize`:@@aiTextToSpeech:AI text to speech`) +
          $localize`:@@googleGoalsTitleSuffix:?`,
      };
    }

    if (key === 'goalInfo') {
      const data = super.customizeStepData(key) || this.steps.goalInfo;
      return {
        ...data,
        title: this.getGoogleGoalTitle(),
        subtitle: this.getGoalSubtitle(),
      };
    }

    return super.customizeStepData(key);
  }

  private getGoalSubtitle(): string {
    const benefitSentence = this.buildBenefitSentence();
    const goalPromise = this.getGoalPromise();

    const line2 =
      $localize`:@@googleGoalSubtitleLine2Prefix:Let's tailor Peech to ` +
      highlightText(goalPromise) +
      $localize`:@@googleGoalSubtitleLine2Suffix:.`;

    // Keep benefitSentence as-is (may contain interpolations from benefit selection),
    // but keep HTML markup outside localizable strings.
    return `${benefitSentence}\n${line2}`;
  }

  private getGoogleGoalTitle(): string {
    const goalId = this.getGoalId();

    const goalTitles: Partial<Record<GoalId, string>> = {
      beMoreProductive:
        $localize`:@@googleGoalTitleProductivePrefix:Peech is the ` +
        highlightText($localize`:@@googleProductivityUpgrade:productivity upgrade`) +
        $localize`:@@googleGoalTitleProductiveSuffix: you were looking for`,
      learnSomethingNew:
        $localize`:@@googleGoalTitleLearnPrefix:Peech is the ` +
        highlightText($localize`:@@googleStudyShortcut:study shortcut`) +
        $localize`:@@googleGoalTitleLearnSuffix: you've been missing`,
      listenOnTheGo:
        $localize`:@@googleGoalTitleOnTheGoPrefix:Peech is the ` +
        highlightText($localize`:@@googleOnTheGoCompanion:on-the-go audio companion`) +
        $localize`:@@googleGoalTitleOnTheGoSuffix: you need`,
      makeReadingEasier:
        $localize`:@@googleGoalTitleReadingEasyPrefix:Peech makes reading ` +
        highlightText($localize`:@@googleEffortlessAgain:effortless again`),
      adhdEyeStrainDyslexia:
        $localize`:@@googleGoalTitleAdhdPrefix:Peech is built for ` +
        highlightText($localize`:@@googleBrainsLearnDifferently:brains that learn differently`),
      everythingIsPerfect:
        $localize`:@@googleGoalTitlePerfectPrefix:Peech keeps your ` +
        highlightText($localize`:@@googleMomentum:momentum`) +
        $localize`:@@googleGoalTitlePerfectSuffix: going`,
    };

    return goalId && goalTitles[goalId]
      ? goalTitles[goalId]!
      : $localize`:@@googleGoalTitleDefaultPrefix:Peech is the ` +
          highlightText($localize`:@@googleAudioCompanion:audio companion`) +
          $localize`:@@googleGoalTitleDefaultSuffix: you were looking for`;
  }

  private buildBenefitSentence(): string {
    const benefits = Array.from(new Set(this.getFrustrationBenefitSnippets()));

    if (benefits.length === 0) {
      const andWord = $localize`:@@and:and`;
      return (
        $localize`:@@googleBenefitsDefaultPrefix:Peech gives you ` +
        highlightText($localize`:@@humanSoundingVoices:human-sounding voices`) +
        ', ' +
        highlightText($localize`:@@cleanImports:clean imports`) +
        `, ${andWord} ` +
        highlightText($localize`:@@roomForLongFormText:room for long-form text`) +
        '.'
      );
    }

    if (benefits.length === 1) {
      return $localize`:@@googleBenefitsOne:Peech gives you ${benefits[0]}:INTERPOLATION:.`;
    }

    return $localize`:@@googleBenefitsTwo:Peech gives you ${benefits[0]}:FIRST_BENEFIT: and ${benefits[1]}:SECOND_BENEFIT:.`;
  }

  private getGoalPromise(): string {
    const goalId = this.getGoalId();

    const goalPromises: Partial<Record<GoalId, string>> = {
      beMoreProductive: $localize`:@@googlePromiseProductive:turn every spare minute into progress`,
      learnSomethingNew: $localize`:@@googlePromiseLearn:turn dense reading into digestible audio`,
      listenOnTheGo: $localize`:@@googlePromiseOnTheGo:keep learning while you move`,
      makeReadingEasier: $localize`:@@googlePromiseReadingEasy:remove the friction from long reads`,
      adhdEyeStrainDyslexia: $localize`:@@googlePromiseAdhd:support your focus without extra effort`,
      everythingIsPerfect: $localize`:@@googlePromisePerfect:keep your growth on autopilot`,
    };

    return goalId && goalPromises[goalId]
      ? goalPromises[goalId]!
      : $localize`:@@googlePromiseDefault:support everything you want to accomplish`;
  }

  private getFrustrationBenefitSnippets(): string[] {
    const selectedFrustrations = this.getSelectedFrustrations();
    const benefitMap: Partial<Record<FrustrationId, string>> = {
      roboticVoices:
        highlightText($localize`:@@humanSoundingVoices:human-sounding voices`) +
        $localize`:@@benefitHumanVoicesSuffix: you'll actually enjoy listening to`,
      pdfsParsedPoorly:
        highlightText($localize`:@@cleanPdfImports:clean PDF and document imports`) +
        $localize`:@@benefitCleanPdfSuffix: without manual fixes`,
      formattingMessed:
        highlightText($localize`:@@layoutPerfectOutput:layout-perfect output`) +
        $localize`:@@benefitLayoutPerfectSuffix: every time`,
      copyPasteWorkflow:
        highlightText($localize`:@@oneTapCapture:one-tap text capture`) +
        $localize`:@@benefitOneTapSuffix: from any source`,
      ocrUnreliable:
        highlightText($localize`:@@accurateOcr:accurate OCR for screenshots`) +
        $localize`:@@benefitAccurateOcrSuffix: when you need it`,
      tooExpensive:
        highlightText($localize`:@@flexiblePlans:flexible plans`) +
        $localize`:@@benefitFlexiblePlansSuffix: that respect your budget`,
      notEnoughVoices:
        highlightText($localize`:@@voices40Plus:40+ voices and accents`) +
        $localize`:@@benefit40VoicesSuffix: ready to go`,
      textLimits:
        highlightText($localize`:@@roomLongFormContent:room for long-form content`) +
        $localize`:@@benefitLongFormContentSuffix: in one go`,
    };

    return selectedFrustrations
      .map(frustration => benefitMap[frustration])
      .filter((benefit): benefit is string => Boolean(benefit));
  }

  private getSelectedFrustrations(): FrustrationId[] {
    const answers = this.getWebQuizAnswers()['frustrations'];

    return Array.isArray(answers) ? (answers as FrustrationId[]) : [];
  }
}
